import React, { Component } from 'react';
import tinycolor from 'tinycolor2';
import seedrandom from 'seedrandom';
import { Point, DIRECTION_NORTH, DIRECTION_EAST, DIRECTION_SOUTH, DIRECTION_WEST } from '../shared/world';

const DRAG_MODE_IDLE = 'idle';
const DRAG_MODE_PANNING = 'panning';
const DRAG_MODE_SELECTING = 'selecting';
const DRAG_MODE_PLACING_ASSET = 'place_asset';
const DRAG_MODE_MOVING = 'moving';
const DRAG_MODE_DUPLICATING = 'duplicating';

export const SELECT_MODE_REPLACE = 'replace';
export const SELECT_MODE_ADD = 'add';
export const SELECT_MODE_REMOVE = 'remove';
export const SELECT_MODE_TOGGLE = 'toggle';

export let mouseToGridHack = undefined;

const CELL_SIZE = 30;
const BLOCK_SIZE = CELL_SIZE * 20;
const MIN_ZOOM = 0.15;
const MAX_ZOOM = 10;

const ASSET_COLOR_MAP = {};

function mousePosition(e, element) {
  var r = element.getBoundingClientRect();
  return new Point(e.clientX - r.left, e.clientY - r.top);
}

function coordsToView(pt, camera) {
  return new Point((pt.x - camera.x) / camera.zoom, (pt.y - camera.y) / camera.zoom);
}

function viewToGrid(pt) {
  let gridX = Math.floor(pt.x / CELL_SIZE);
  let gridY = Math.floor(pt.y / CELL_SIZE);
  return new Point(gridX, gridY);
}

function isPointInBounds(point, bounds) {
  if (point.x < bounds.left) return false;
  if (point.y < bounds.top) return false;
  if (point.x > bounds.right) return false;
  if (point.y > bounds.bottom) return false;
  return true;
}

class Camera {
  constructor(x = 0, y = 0, zoom = 1) {
    this.x = x;
    this.y = y;
    this.zoom = zoom;
  }
}

export default class Canvas extends Component {
  constructor(props) {
    super(props);
    this.state = {
      camera: new Camera(20, 20, 0.5),
    };
    this._onMouseDown = this._onMouseDown.bind(this);
    this._onMouseMove = this._onMouseMove.bind(this);
    this._onMouseDrag = this._onMouseDrag.bind(this);
    this._onMouseUp = this._onMouseUp.bind(this);
    this._onDoubleClick = this._onDoubleClick.bind(this);
    this._onWheel = this._onWheel.bind(this);
    this._onKeyDown = this._onKeyDown.bind(this);
    this._onKeyUp = this._onKeyUp.bind(this);
    this._onResize = this._onResize.bind(this);
    this._spaceDown = false;
    this._dragMode = DRAG_MODE_IDLE;
    this._selectMode = SELECT_MODE_ADD;
    this._prevPoint = undefined;
    this._currentPoint = undefined;
    mouseToGridHack = this._mouseToGrid.bind(this);
    this._assetGhostAsset = null;
    this._assetGhostPoint = new Point(0, 0);
    this._assetGhostInBounds = false;
    this._draggingCellds = null;
    this._offsetPoint = new Point(0, 0);
  }

  componentDidMount() {
    this._canvas = document.getElementById('viewer');
    this._ctx = this._canvas.getContext('2d');
    this._draw();
    window.addEventListener('keydown', this._onKeyDown);
    window.addEventListener('keyup', this._onKeyUp);
    window.addEventListener('resize', this._onResize);
  }

  componentWillUnmount() {
    window.removeEventListener('keydown', this._onKeyDown);
    window.removeEventListener('keyup', this._onKeyUp);
    window.removeEventListener('resize', this._onResize);
  }

  componentDidUpdate(prevProps) {
    if (prevProps.assetGhost === null && this.props.assetGhost) {
      this._dragMode = DRAG_MODE_PLACING_ASSET;
      this._assetGhostPoint = { x: -100, y: -100 };
      this._assetGhostAsset = this.props.assetGhost.asset;
      this._assetGhostInBounds = false;
      window.addEventListener('mousemove', this._onMouseDrag);
      window.addEventListener('mouseup', this._onMouseUp);
    }
    this._draw();
  }

  _mouseToGrid(e) {
    const canvas = this._canvas;
    const mousePos = mousePosition(e, canvas);
    const viewPos = coordsToView(mousePos, this.state.camera);
    const gridPos = viewToGrid(viewPos);
    return gridPos;
  }

  _onWheel(e) {
    e.preventDefault();
    const camera = this.state.camera;
    const canvas = this._canvas;
    const pos = mousePosition(e, canvas);
    const dir = e.deltaY < 0 ? -1 : 1;
    let zoomDelta = 1 - dir * 0.1;
    const newZoom = camera.zoom * zoomDelta;
    if (newZoom < MIN_ZOOM) {
      zoomDelta = MIN_ZOOM / camera.zoom;
    } else if (newZoom > MAX_ZOOM) {
      zoomDelta = MAX_ZOOM / camera.zoom;
    }
    const camX = camera.x - (pos.x - camera.x) * (zoomDelta - 1);
    const camY = camera.y - (pos.y - camera.y) * (zoomDelta - 1);
    const camZoom = camera.zoom * zoomDelta;
    this.setState({ camera: new Camera(camX, camY, camZoom) });
  }

  _onMouseDown(e) {
    e.preventDefault();

    if (this._dragMode === DRAG_MODE_MOVING || this._dragMode === DRAG_MODE_DUPLICATING) {
      if (e.button === 0) {
        const dx = Math.round(this._offsetPoint.x / this.state.camera.zoom / CELL_SIZE);
        const dy = Math.round(this._offsetPoint.y / this.state.camera.zoom / CELL_SIZE);
        if (this._dragMode === DRAG_MODE_MOVING) {
          this.props.onMoveCells(this._draggingCellds, dx, dy);
        } else if (this._dragMode === DRAG_MODE_DUPLICATING) {
          this.props.onDuplicateCells(this._draggingCellds, dx, dy);
        }
      }
      this._dragMode = DRAG_MODE_IDLE;
      window.removeEventListener('mouseup', this._onMouseUp);
      window.removeEventListener('mousemove', this._onMouseDrag);
      this._draw();
      return;
    }

    if (this._spaceDown || e.button === 2 || e.button === 1) {
      this._dragMode = DRAG_MODE_PANNING;
    } else if (e.button === 0) {
      this._dragMode = DRAG_MODE_SELECTING;
    } else {
      this._dragMode = DRAG_MODE_IDLE;
      return;
    }

    const pos = mousePosition(e, this._canvas);
    this._startPoint = new Point(e.clientX, e.clientY);
    this._offsetPoint = new Point(0, 0);
    this._prevPoint = new Point(e.clientX, e.clientY);
    this._startCoords = coordsToView(pos, this.state.camera);
    const gridPt = viewToGrid(this._startCoords);
    if (this._dragMode === DRAG_MODE_SELECTING) {
      const cellId = this.props.world.pointToCellId(gridPt);
      if (cellId !== -1) {
        if (e.shiftKey) {
          if (this.props.selection.has(cellId)) {
            this._selectMode = SELECT_MODE_REMOVE;
          } else {
            this._selectMode = SELECT_MODE_ADD;
          }
          this.props.onSelectCells([cellId], this._selectMode);
        } else {
          this.props.onSelectCells([cellId], SELECT_MODE_REPLACE);
          this._selectMode = SELECT_MODE_ADD;
        }
      }
    }
    window.addEventListener('mousemove', this._onMouseDrag);
    window.addEventListener('mouseup', this._onMouseUp);
  }

  _onMouseMove(e) {
    this._currentPoint = new Point(e.clientX, e.clientY);
  }

  _onDoubleClick(e) {
    e.preventDefault();
    const pos = mousePosition(e, this._canvas);
    this._startCoords = coordsToView(pos, this.state.camera);
    const gridPt = viewToGrid(this._startCoords);
    const entityId = this.props.world.pointToEntityId(gridPt);
    if (!entityId) return;
    const entity = this.props.world.findEntityById(entityId);
    if (!entity) return;
    this.props.onSelectCells(entity.cellIds);
  }

  _onMouseDrag(e) {
    e.preventDefault();
    const x = e.clientX;
    const y = e.clientY;
    this._canvas.getBoundingClientRect();
    if (this._dragMode === DRAG_MODE_PANNING) {
      const dx = x - this._prevPoint.x;
      const dy = y - this._prevPoint.y;
      this.setState({
        camera: new Camera(this.state.camera.x + dx, this.state.camera.y + dy, this.state.camera.zoom),
      });
    } else if (this._dragMode === DRAG_MODE_SELECTING) {
      const pos = mousePosition(e, this._canvas);
      const endCoords = coordsToView(pos, this.state.camera);
      const startGridPt = viewToGrid(this._startCoords);
      const endGridPt = viewToGrid(endCoords);
      if (startGridPt.x > endGridPt.x) {
        let swap = startGridPt.x;
        startGridPt.x = endGridPt.x;
        endGridPt.x = swap;
      }
      if (startGridPt.y > endGridPt.y) {
        let swap = startGridPt.y;
        startGridPt.y = endGridPt.y;
        endGridPt.y = swap;
      }
      const selection = [];
      for (let y = startGridPt.y; y <= endGridPt.y; y++) {
        for (let x = startGridPt.x; x <= endGridPt.x; x++) {
          selection.push(this.props.world.pointToCellId(new Point(x, y)));
        }
      }
      this.props.onSelectCells(selection, this._selectMode);
    } else if (this._dragMode === DRAG_MODE_MOVING || this._dragMode === DRAG_MODE_DUPLICATING) {
      this._offsetPoint.x = x - this._startPoint.x;
      this._offsetPoint.y = y - this._startPoint.y;
      this._draw();
    } else if (this._dragMode === DRAG_MODE_PLACING_ASSET) {
      const pos = mousePosition(e, this._canvas);
      const coords = coordsToView(pos, this.state.camera);
      this._assetGhostPoint = viewToGrid(coords);
      this._draw();
    }
    this._prevPoint = new Point(x, y);
  }

  _onMouseUp(e) {
    e.preventDefault();

    if (this._dragMode === DRAG_MODE_PLACING_ASSET) {
      // Check if the coordinates are in view.
      if (isPointInBounds({ x: e.clientX, y: e.clientY }, this._canvas.getBoundingClientRect())) {
        this.props.onPlaceAsset(this._assetGhostAsset, this._assetGhostPoint);
        // this._assetGhostPoint = { x: 0, y: 0 };
        // this._assetGhostAsset = null;
      }
    }

    window.removeEventListener('mouseup', this._onMouseUp);
    window.removeEventListener('mousemove', this._onMouseDrag);
    this._dragMode = DRAG_MODE_IDLE;
  }

  _onKeyDown(e) {
    if (e.keyCode === 32) {
      this._spaceDown = true;
    } else if (e.key === 'g') {
      this._startDrag(DRAG_MODE_MOVING);
    } else if (e.key === 'D') {
      this._startDrag(DRAG_MODE_DUPLICATING);
    } else if (e.key === 'Escape') {
      this._dragMode = DRAG_MODE_IDLE;
      this._draggingCellds = [];
      this._draw();
      window.removeEventListener('mousemove', this._onMouseDrag);
      window.removeEventListener('mouseup', this._onMouseUp);
    }
  }

  _onKeyUp(e) {
    if (e.keyCode === 32) {
      this._spaceDown = false;
    }
  }

  _onResize() {
    this._draw();
  }

  _startDrag(dragMode) {
    this._startPoint = new Point(this._currentPoint.x, this._currentPoint.y);
    this._offsetPoint = new Point(0, 0);
    this._dragMode = dragMode;
    const entities = this.props.activeLayer.findEntitiesForCells(Array.from(this.props.selection));
    this._draggingCellds = this.props.activeLayer.findCellIdsForEntities(Array.from(entities));
    window.addEventListener('mousemove', this._onMouseDrag);
    window.addEventListener('mouseup', this._onMouseUp);
    this._draw();
  }

  _draw() {
    const canvas = this._canvas;
    const ctx = this._ctx;
    const { world, selection, activeLayer, assetGhost } = this.props;
    if (!world) return;
    const { camera } = this.state;
    const { layers } = world;
    const bounds = canvas.getBoundingClientRect();
    canvas.width = bounds.width * window.devicePixelRatio;
    canvas.height = bounds.height * window.devicePixelRatio;
    canvas.style.width = `${bounds.width}px`;
    canvas.style.height = `${bounds.height}px`;
    const scale = camera.zoom * window.devicePixelRatio;
    const tx = camera.x * window.devicePixelRatio;
    const ty = camera.y * window.devicePixelRatio;
    ctx.setTransform(scale, 0, 0, scale, tx, ty);
    this._drawGrid(canvas, ctx, world);
    for (const layer of layers) {
      if (!layer.editorVisible) continue;
      for (const entity of layer.entities) {
        this._drawEntity(ctx, entity, layer === activeLayer);
        // this._drawEntityIcon(ctx, world, entity);
        // this._drawEntityNotes(ctx, world, entity);
      }
    }
    // this._drawOwners(ctx, world);
    this._drawSelection(ctx, selection);
    if (this._dragMode === DRAG_MODE_PLACING_ASSET) {
      this._drawAssetGhost(ctx, assetGhost);
    }
    if (this._dragMode === DRAG_MODE_MOVING || this._dragMode === DRAG_MODE_DUPLICATING) {
      this._drawDraggingCells(ctx, this._draggingCellds);
    }
    // if (objectMode === 'hints') {
    //   this._drawHints(ctx, world);
    // }
    // if (this.props.showGardenPatches) {
    //   this._drawGardenPatches(ctx, world);
    // }
    // if (this.props.showOcclusions) {
    //   this._drawOcclusions(ctx, world);
    // }
  }

  _drawGrid(canvas, ctx, world) {
    ctx.lineWidth = 1;
    ctx.strokeStyle = '#ccc';
    ctx.beginPath();
    for (let x = 0; x <= world.width * CELL_SIZE; x += CELL_SIZE) {
      ctx.moveTo(x - 1, 0);
      ctx.lineTo(x - 1, world.height * CELL_SIZE);
    }
    for (let y = 0; y <= world.height * CELL_SIZE; y += CELL_SIZE) {
      ctx.moveTo(0, y - 1);
      ctx.lineTo(world.width * CELL_SIZE, y - 1);
    }
    ctx.stroke();

    ctx.lineWidth = 3;
    ctx.strokeStyle = '#ccc';
    ctx.beginPath();
    for (let x = 0; x <= world.width * CELL_SIZE; x += BLOCK_SIZE) {
      ctx.moveTo(x - 1, 0);
      ctx.lineTo(x - 1, world.height * CELL_SIZE);
    }
    for (let y = 0; y <= world.height * CELL_SIZE; y += BLOCK_SIZE) {
      ctx.moveTo(0, y - 1);
      ctx.lineTo(world.width * CELL_SIZE, y - 1);
    }
    ctx.stroke();
  }

  _colorForEntityAsset(entity, assetId) {
    const cachedColor = ASSET_COLOR_MAP[assetId];
    if (cachedColor) return cachedColor;
    const rng = seedrandom(assetId);
    let color = tinycolor(entity.color);
    let { h, s, l } = color.toHsl();
    s += (rng() - 0.5) * 0.1;
    l += (rng() - 0.5) * 0.1;
    return tinycolor({ h, s, l }).toHex8String();
  }

  _drawEntity(ctx, entity, isActiveLayer) {
    ctx.globalAlpha = isActiveLayer ? 1.0 : 0.2;
    for (let i = 0; i < entity.cellIds.length; i++) {
      const cellId = entity.cellIds[i];
      const assetId = entity.assetIds[i];
      const direction = entity.directions[i];
      const pt = this._cellIdToView(cellId);
      ctx.fillStyle = this._colorForEntityAsset(entity, assetId);
      ctx.fillRect(pt.x, pt.y, 29, 29);
      ctx.fillStyle = 'rgba(0, 0, 0, 0.5)';
      if (direction === DIRECTION_NORTH) {
        ctx.fillRect(pt.x + 15, pt.y, 1, 5);
      } else if (direction === DIRECTION_SOUTH) {
        ctx.fillRect(pt.x + 15, pt.y + 24, 1, 5);
      } else if (direction === DIRECTION_WEST) {
        ctx.fillRect(pt.x, pt.y + 15, 5, 1);
      } else if (direction === DIRECTION_EAST) {
        ctx.fillRect(pt.x + 24, pt.y + 15, 5, 1);
      }
    }

    ctx.globalAlpha = 1.0;
  }

  _drawSelection(ctx, selection) {
    const selectionArray = Array.from(selection);
    if (this._dragMode === DRAG_MODE_MOVING || this._dragMode === DRAG_MODE_DUPLICATING) {
      ctx.fillStyle = 'rgba(193, 193, 241, 0.2)';
    } else {
      ctx.fillStyle = 'rgba(193, 193, 241, 0.8)';
    }

    for (const cellId of selectionArray) {
      const pt = this._cellIdToView(cellId);
      if (pt.x < 0 && pt.y < 0) continue;
      ctx.fillRect(pt.x, pt.y, 30, 30);
    }
  }

  _drawAssetGhost(ctx, ghost) {
    if (!ghost) return;
    // const pos = mousePosition({ clientX: ghost.x, clientY: ghost.y }, this._canvas);
    //this._startPoint = new Point(e.clientX, e.clientY);
    //this._prevPoint = new Point(e.clientX, e.clientY);
    // const coords = coordsToView(pos, this.state.camera);
    // const pt = viewToGrid(coords);
    const pt = this._assetGhostPoint;
    ctx.fillStyle = 'rgba(193, 193, 241, 0.8)';
    const occupied = this.props.activeLayer.boxOccupied(pt.x, pt.y, pt.x + ghost.asset.width - 1, pt.y + ghost.asset.height - 1);
    if (occupied) {
      ctx.fillStyle = 'rgba(255, 255, 255, 0.5)';
    } else {
      ctx.fillStyle = 'gray';
    }
    ctx.fillRect(pt.x * CELL_SIZE, pt.y * CELL_SIZE, ghost.asset.width * CELL_SIZE, ghost.asset.height * CELL_SIZE);
  }

  _drawDraggingCells(ctx, cellIds) {
    const dx = Math.round(this._offsetPoint.x / this.state.camera.zoom / CELL_SIZE);
    const dy = Math.round(this._offsetPoint.y / this.state.camera.zoom / CELL_SIZE);
    const [newCellIds, valid] = this.props.world.offsetCellIds(cellIds, dx, dy);
    let toCheckCellIds;
    if (this._dragMode === DRAG_MODE_MOVING) {
      // When moving, we can move over cells we already occupy.
      toCheckCellIds = newCellIds.filter((id) => !cellIds.includes(id));
    } else {
      toCheckCellIds = newCellIds;
    }
    const occupied = valid && this.props.activeLayer.someCellsOccupied(toCheckCellIds);
    ctx.fillStyle = occupied ? 'red' : 'rgba(193, 193, 241, 0.8)';
    for (const cellId of newCellIds) {
      const pt = this._cellIdToView(cellId);
      if (pt.x < 0 && pt.y < 0) continue;
      ctx.fillRect(pt.x, pt.y, CELL_SIZE, CELL_SIZE);
    }
  }

  _worldToView(pt) {
    return new Point(pt.x * CELL_SIZE, pt.y * CELL_SIZE);
  }

  _cellIdToView(cellId) {
    const pt = this.props.world.cellIdToPoint(cellId);
    return this._worldToView(pt);
  }

  render() {
    return (
      <div className="canvas__wrapper">
        <canvas
          id="viewer"
          className="viewer__canvas"
          onMouseDown={this._onMouseDown}
          onMouseMove={this._onMouseMove}
          onDoubleClick={this._onDoubleClick}
          onWheel={this._onWheel}
          onContextMenu={(e) => e.preventDefault()}
        />
      </div>
    );
  }
}
